/**
 * E-signature placement maths for the field editor.
 *
 * The functions in the first half are ports of
 * api/app/Services/Esign/KeywordPlacement.php (itself a port of the legacy
 * stamper's library). They keep the same operation order so the IEEE doubles
 * are identical; lib/esign/geometry.test.ts replays the golden vectors in
 * api/tests/Fixtures/esign/golden-vectors.json with exact equality.
 *
 * Units, as the stamper reads them:
 *  - Anchor (xPt, yPt): PDF points, bottom-left origin.
 *  - Field offsets (signatureX / signatureY): PDF points, added WITHOUT the
 *    page scale factor, +Y down. A zero offset adds nothing (the `!= 0` guard).
 *  - Width: millimetres; blank or 0 means 50.
 *  - A page is drawn 210 mm wide (pageScaleFactor = 210 / widthMm).
 *  - Output positions are millimetres from the drawn page's top-left corner.
 *
 * The second half converts between those millimetres and screen pixels for a
 * page rendered W px wide (px per mm = W / 210).
 *
 * Copied from corp-dashboard/lib/esign/geometry.ts.
 */

import { phpTrim } from './match';

export const TARGET_WIDTH_MM = 210.0;
export const POINTS_TO_MM = 25.4 / 72.0;
export const DEFAULT_SIGNATURE_WIDTH_MM = 50;

/** Text fields are stamped in Helvetica Bold 8 pt on a 3 mm line (TCPDF cell padding 0). */
export const TEXT_FONT_PT = 8;
export const TEXT_LINE_MM = 3;
/** The portal's signature pad is 800x150 and the initials pad 200x150; the image is drawn `width` mm wide. */
export const SIGNATURE_ASPECT = 150 / 800;
export const INITIALS_ASPECT = 150 / 200;

export interface PageMetrics {
  pageScaleFactor: number;
  scaledPageHeightMm: number;
  pointsToMm: number;
  coordinateConversionFactor: number;
}

export function pageMetrics(originalWidthMm: number, originalHeightMm: number): PageMetrics {
  const pageScaleFactor = TARGET_WIDTH_MM / originalWidthMm;
  const scaledPageHeightMm = originalHeightMm * pageScaleFactor;
  const pointsToMm = POINTS_TO_MM;
  return {
    pageScaleFactor,
    scaledPageHeightMm,
    pointsToMm,
    coordinateConversionFactor: pointsToMm * pageScaleFactor,
  };
}

export interface PlacementMm {
  x: number;
  y: number;
  anchorX: number;
  anchorY: number;
  pageScaleFactor: number;
  scaledPageHeightMm: number;
}

export function placementMm(
  xPt: number,
  yPt: number,
  xOffsetPt: number,
  yOffsetPt: number,
  originalWidthMm: number,
  originalHeightMm: number,
): PlacementMm {
  const m = pageMetrics(originalWidthMm, originalHeightMm);

  let finalX = xPt * m.coordinateConversionFactor;
  let finalY = m.scaledPageHeightMm - yPt * m.coordinateConversionFactor;
  const anchorX = finalX;
  const anchorY = finalY;

  if (xOffsetPt != 0) {
    finalX += xOffsetPt * m.pointsToMm;
  }
  if (yOffsetPt != 0) {
    finalY += yOffsetPt * m.pointsToMm;
  }

  return {
    x: finalX,
    y: finalY,
    anchorX,
    anchorY,
    pageScaleFactor: m.pageScaleFactor,
    scaledPageHeightMm: m.scaledPageHeightMm,
  };
}

/** The synthetic anchor of a fixed-spot field: the drawn page's top-left corner. */
export function freePlaceAnchor(originalHeightMm: number): { xPt: number; yPt: number } {
  return { xPt: 0.0, yPt: originalHeightMm / POINTS_TO_MM };
}

/** PHP round() to an integer: half away from zero. */
function phpRound(value: number): number {
  return value < 0 ? -Math.round(-value) : Math.round(value);
}

/**
 * Where a new fixed-spot field is first put: the lower third of the page,
 * stepped down and right by 6 mm for each fixed field already on the page.
 */
export function freePlaceDefaultOffsetPt(originalWidthMm: number, originalHeightMm: number, index = 0): { xPt: number; yPt: number } {
  const metrics = pageMetrics(originalWidthMm, originalHeightMm);
  const stepMm = 6.0 * index;

  const xMm = Math.min(0.2 * TARGET_WIDTH_MM + stepMm, TARGET_WIDTH_MM - 10.0);
  const yMm = Math.min(0.7 * metrics.scaledPageHeightMm + stepMm, metrics.scaledPageHeightMm - 10.0);

  return { xPt: phpRound(xMm / POINTS_TO_MM), yPt: phpRound(yMm / POINTS_TO_MM) };
}

/** PHP's leading-whitespace set for numeric strings. */
const PHP_NUMERIC_WS = /^[ \t\n\r\v\f]*/;

/** PHP 8 is_numeric() for a string (leading and trailing whitespace allowed). */
function phpIsNumericString(value: string): boolean {
  return /^[ \t\n\r\v\f]*[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?[ \t\n\r\v\f]*$/.test(value);
}

/** PHP (int) / intval() of a string. */
function phpIntvalString(value: string): number {
  if (phpIsNumericString(value)) return Math.trunc(Number(value.trim()));
  const lead = value.replace(PHP_NUMERIC_WS, '').match(/^[+-]?\d+/);
  return lead ? Number(lead[0]) : 0;
}

/** PHP (int) of a scalar or null. */
function phpInt(value: unknown): number {
  if (value === null || value === undefined || value === false) return 0;
  if (value === true) return 1;
  if (typeof value === 'number') return Number.isFinite(value) ? Math.trunc(value) : 0;
  return phpIntvalString(String(value));
}

/** Width (mm) a signature or initials image is drawn at, from the stored value. */
export function signatureWidthMm(width: unknown): number {
  // PHP 8: `$w == 0 || $w == ''`. null == 0 is true; a non-numeric string
  // is not == 0; a numeric string compares by value.
  let isDefault: boolean;
  if (width === null || width === undefined || width === false) {
    isDefault = true;
  } else if (typeof width === 'number') {
    isDefault = width == 0;
  } else {
    const s = String(width);
    isDefault = s === '' || (phpIsNumericString(s) && Number(s.trim()) == 0);
  }
  return isDefault ? DEFAULT_SIGNATURE_WIDTH_MM : phpInt(width);
}

/** A fixed spot on a page instead of next to words: blank words and a page of 1 or more. */
export function keywordIsFreePlaced(tabKeywords: string, signaturePageNum: unknown): boolean {
  return phpTrim(tabKeywords) === '' && phpInt(signaturePageNum) >= 1;
}

/** The page a stored field names, as PHP's (int) reads it. */
export function pageNumberOf(signaturePageNum: unknown): number {
  return phpInt(signaturePageNum);
}

export type WordsWarning = 'empty' | 'shortcode' | 'whitespace';

/** Problems the editor points out for a field's words. */
export function keywordWarnings(keyword: string): WordsWarning[] {
  const warnings: WordsWarning[] = [];
  if (phpTrim(keyword) === '') {
    warnings.push('empty');
    return warnings;
  }
  if (/\$\{[^}]*\}|\{[A-Za-z][A-Za-z0-9_]*\}/.test(keyword)) {
    warnings.push('shortcode');
  }
  if (keyword !== phpTrim(keyword)) {
    warnings.push('whitespace');
  }
  return warnings;
}

// ---- Element size as the stamp draws it -------------------------------------

export function isSignatureType(type: string): boolean {
  return type === 'signature' || type === 'initials';
}

/** The aspect (height / width) of a signature-type image. */
export function aspectForType(type: string): number {
  return type === 'initials' ? INITIALS_ASPECT : SIGNATURE_ASPECT;
}

/**
 * The drawn element in millimetres. Signature and initials: `width` mm wide,
 * height from the pad's aspect. Text types: a 3 mm line whose width follows the
 * text (null here: the browser lays the text out at the stamp's font size).
 */
export function elementSizeMm(type: string, width: unknown): { widthMm: number | null; heightMm: number } {
  if (isSignatureType(type)) {
    const w = signatureWidthMm(width);
    return { widthMm: w, heightMm: w * aspectForType(type) };
  }
  return { widthMm: null, heightMm: TEXT_LINE_MM };
}

/** Font size in mm of the stamped text (8 pt). */
export const TEXT_FONT_MM = TEXT_FONT_PT * POINTS_TO_MM;

// ---- Screen conversions ------------------------------------------------------

/** Pixels per output millimetre for a page rendered `renderWidthPx` wide. */
export function pxPerMm(renderWidthPx: number): number {
  return renderWidthPx / TARGET_WIDTH_MM;
}

export function mmToPx(mm: number, renderWidthPx: number): number {
  return mm * pxPerMm(renderWidthPx);
}

export function pxToMm(px: number, renderWidthPx: number): number {
  return px / pxPerMm(renderWidthPx);
}

/** A drag of `deltaPx` on screen as a change of offset in points (offsets are not page-scaled). */
export function dragDeltaPxToPt(deltaPx: number, renderWidthPx: number): number {
  return pxToMm(deltaPx, renderWidthPx) / POINTS_TO_MM;
}

/** A change of offset in points as screen pixels. */
export function offsetPtToPx(deltaPt: number, renderWidthPx: number): number {
  return mmToPx(deltaPt * POINTS_TO_MM, renderWidthPx);
}

/**
 * Offset rounding policy: whole points, half away from zero.
 *
 * This is what the legacy editor wrote after a drag or a nudge (Math.round on
 * points), and one point (0.35 mm) is finer than anyone can place by hand. A
 * value typed into the X / Y inputs is kept as typed and only rounded when it
 * is next moved by dragging or the arrow keys.
 */
export function roundOffsetPt(pt: number): number {
  const r = phpRound(pt);
  return r === 0 ? 0 : r; // no -0
}

/** Width after a resize: whole millimetres, at least 1 (legacy: max(1, round(px / pxPerMm))). */
export function roundWidthMm(mm: number): number {
  return Math.max(1, Math.round(mm));
}

/** A stored offset string as a number; anything unreadable is 0 (legacy parseFloat || 0). */
export function offsetValue(value: string | number | null | undefined): number {
  const n = typeof value === 'number' ? value : parseFloat(String(value ?? ''));
  return Number.isFinite(n) ? n : 0;
}

// ---- Words on the page: highlight rectangles ---------------------------------

/** A positioned text item from pdf.js (transform[4], transform[5] = x, y in points). */
export interface PageTextItem {
  transform: number[];
  width: number;
  height: number;
}

export interface RunGeometry {
  xPt: number;
  yPt: number;
  wPt: number;
  hPt: number;
  tier: 'a' | 'b' | 'c';
}

/**
 * Where a run of text sits on the page. Server runs only carry a start point;
 * pdf.js items carry width and height, so borrow them when they line up:
 *  a) an item starting at the same point          -> its width and height
 *  b) an item on the same line spanning the point -> its height, width to its end
 *  c) nothing known                               -> a rough box from the text length
 */
export function geometryForRun(run: { xPt: number; yPt: number; text: string }, items: PageTextItem[] | null): RunGeometry {
  const chars = Math.max(1, String(run.text || '').length);
  if (items && items.length) {
    for (const it of items) {
      const e = it.transform[4];
      const f = it.transform[5];
      if (Math.abs(e - run.xPt) <= 1 && Math.abs(f - run.yPt) <= 1) {
        const h = it.height > 0 ? it.height : 10;
        return { xPt: run.xPt, yPt: run.yPt, wPt: it.width > 0 ? it.width : chars * 0.5 * h, hPt: h, tier: 'a' };
      }
    }
    for (const it of items) {
      const e = it.transform[4];
      const f = it.transform[5];
      if (Math.abs(f - run.yPt) <= 1 && e <= run.xPt + 0.5 && e + (it.width || 0) >= run.xPt) {
        const h = it.height > 0 ? it.height : 10;
        const remaining = e + (it.width || 0) - run.xPt;
        let w = Math.min(chars * 0.5 * h, remaining);
        if (w < 2) w = chars * 0.5 * h;
        return { xPt: run.xPt, yPt: run.yPt, wPt: w, hPt: h, tier: 'b' };
      }
    }
  }
  return { xPt: run.xPt, yPt: run.yPt, wPt: chars * 5, hPt: 10, tier: 'c' };
}

/** Screen rectangle (px, relative to the page's top-left) for a run's geometry. */
export function runRectPx(
  geom: RunGeometry,
  page: { widthMm: number; heightMm: number },
  renderWidthPx: number,
): { left: number; top: number; width: number; height: number } {
  const px = pxPerMm(renderWidthPx);
  const s = TARGET_WIDTH_MM / page.widthMm;
  const ccf = POINTS_TO_MM * s;
  const baselineMm = page.heightMm * s - geom.yPt * ccf;
  const hMm = geom.hPt * ccf;
  return {
    left: geom.xPt * ccf * px,
    top: (baselineMm - 0.8 * hMm) * px,
    width: Math.max(2, geom.wPt * ccf * px),
    height: Math.max(2, hMm * px),
  };
}
